/**
 * Landing FAQ — product boundaries.
 * Dollar ladder lives on `/pricing` only.
 */

'use client'

import React, { useEffect } from 'react'
import Link from 'next/link'

type FaqItem = {
  q: string
  a: React.ReactNode
}

const FAQS: readonly FaqItem[] = [
  {
    q: 'What Is Mechagora?',
    a: (
      <>
        A client trading workspace for seeing markets, positions, activity, supported accounts, and
        agent controls in one place.
      </>
    ),
  },
  {
    q: 'What Can I Customize?',
    a: 'Every plan includes appearance presets. Paid plans add account-synced layouts and workspace settings, with higher limits on higher plans.',
  },
  {
    q: 'Can I Connect My Trading Accounts?',
    a: 'Desk and Pro support configured broker and exchange connections. The product shows which connections are available to you.',
  },
  {
    q: 'What Is Included for Free?',
    a: 'Free Desk includes market and portfolio views, one named paper agent, 200 paper-only Desk Credits each month, and research chat that uses those credits. It has no live execution or live autonomous agents.',
  },
  {
    q: 'What Are Desk Credits?',
    a: 'Desk Credits are the cost-to-run meter for agent activity and model use. Free Desk credits work only for paper activity and research. Paused agents burn nothing, and their configuration is kept.',
  },
  {
    q: 'Can Free Desk Place Orders?',
    a: 'Free Desk can record clearly labeled paper activity, but it cannot route live orders. Manual live execution starts on Operator at $49.99 per month.',
  },
  {
    q: 'Do I Have to Use Agents?',
    a: (
      <>
        No. You can use <span translate="no">Mechagora</span> for market and portfolio views. Agent
        research is included on Free, full agent chat starts on Operator, and live autonomous
        agents start on Desk. You remain in control with start, pause, and stop.
      </>
    ),
  },
  {
    q: 'What Happens to My Paper Agent If I Upgrade?',
    a: 'Its name, mandate, and history stay with the desk. Operator adds full chat and manual live execution. Desk at $99.99 per month can bring the first agent seat live.',
  },
  {
    q: 'Is This a Signals or Copy-Trading Marketplace?',
    a: (
      <>
        No. Strategy libraries help configure your own tools and agents.{' '}
        <span translate="no">Mechagora</span> does not sell third-party signals or copy other
        traders.
      </>
    ),
  },
  {
    q: 'Do You Promise Returns or Performance?',
    a: (
      <>
        No. <span translate="no">Mechagora</span> provides workspace and agent tools. It does not
        promise returns, guaranteed P&L, or “AI alpha.”
      </>
    ),
  },
  {
    q: 'Where Do I Compare Plans?',
    a: (
      <>
        Open{' '}
        <Link href="/pricing" className="lp-inline-link">
          Pricing
        </Link>{' '}
        to compare features, list prices, and Pro+ options.
      </>
    ),
  },
]

function faqId(question: string): string {
  return `faq-${question
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}`
}

export const FaqSection: React.FC = () => {
  useEffect(() => {
    const applyHash = () => {
      const hash = window.location.hash.slice(1)
      if (!hash.startsWith('faq-') || hash === 'faq') return
      const target = document.getElementById(hash)
      if (target instanceof HTMLDetailsElement) target.open = true
    }
    applyHash()
    window.addEventListener('hashchange', applyHash)
    return () => window.removeEventListener('hashchange', applyHash)
  }, [])

  const syncFaqHash = (item: HTMLDetailsElement) => {
    const url = new URL(window.location.href)
    if (item.open) {
      url.hash = item.id
    } else if (url.hash === `#${item.id}`) {
      url.hash = 'faq'
    } else {
      return
    }
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
  }

  return (
    <section id="faq" className="lp-section lp-section-faq" aria-labelledby="faq-title">
      <div className="lp-band lp-band-faq">
        <header className="lp-band-head">
          <div className="lp-band-head-copy">
            <p className="lp-kicker">FAQ</p>
            <h2 id="faq-title" className="lp-section-title">
              Questions Before You Open a Desk?
            </h2>
            <p className="lp-section-lede">
              Find out what is included, how paper trading works, and when to upgrade. See{' '}
              <Link href="/pricing" className="lp-inline-link">
                Pricing
              </Link>
              {' '}or visit{' '}
              <Link href="/help" className="lp-inline-link">
                Help
              </Link>
              .
            </p>
          </div>
        </header>

        <div className="lp-faq-list">
          {FAQS.map((item) => (
            <details
              key={item.q}
              id={faqId(item.q)}
              className="lp-faq-item"
              onToggle={(event) => syncFaqHash(event.currentTarget)}
            >
              <summary className="lp-faq-q">
                <h3>
                  {item.q.includes('Mechagora') ? (
                    <>
                      What Is <span translate="no">Mechagora?</span>
                    </>
                  ) : (
                    item.q
                  )}
                </h3>
              </summary>
              <p className="lp-faq-a">{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  )
}
